import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle2, ShieldCheck } from 'lucide-react';
import { AppHeader } from '@/components/layout/AppHeader';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { submitVerificationRequest } from '@/lib/api/profiles';
import { useAuth } from '@/lib/hooks/useAuth';

const schema = z.object({
  linkedin_url: z.string().min(1, 'LinkedIn URL is required').url('Enter a valid LinkedIn URL'),
  note: z.string().max(500).optional(),
});
type FormValues = z.infer<typeof schema>;

export function RequestVerification() {
  const { loaded, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromSetup = (location.state as { fromSetup?: boolean } | null)?.fromSetup === true;
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { linkedin_url: '', note: '' },
  });

  if (loaded && !isAuthenticated) {
    navigate('/signin', { replace: true });
    return null;
  }

  const onSubmit = async (values: FormValues) => {
    setSubmitError(null);
    try {
      await submitVerificationRequest(values.linkedin_url, values.note || undefined);
      setSubmitted(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Submission failed.';
      if (msg.includes('already_verified')) {
        setSubmitError('Your account is already verified.');
      } else if (msg.includes('pending_request_exists')) {
        setSubmitError('A verification request is already pending for your account.');
      } else if (msg.includes('no_profile_found')) {
        setSubmitError('Please complete your profile setup before requesting verification.');
      } else {
        setSubmitError(msg);
      }
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-cream">
      <AppHeader />
      <main className="flex-1">
        <div className="container max-w-lg py-12">
          {submitted ? (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gold/20">
                    <ShieldCheck className="h-5 w-5 text-gold-dark" aria-hidden="true" />
                  </span>
                  <CardTitle>Verification request submitted</CardTitle>
                </div>
                <CardDescription className="mt-2">
                  We'll review your LinkedIn and get back to you shortly. You can check the status
                  on your{' '}
                  <Link to="/me/edit" className="text-gold-dark underline underline-offset-2">
                    profile edit page
                  </Link>
                  .
                </CardDescription>
              </CardHeader>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                {fromSetup && (
                  <div className="mb-4 flex items-center gap-2 rounded-xl bg-success/10 px-4 py-3 text-sm text-success">
                    <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
                    Profile created — one more step to unlock your Verified badge.
                  </div>
                )}
                <CardTitle>Request verification</CardTitle>
                <CardDescription>
                  Submit your LinkedIn or Facebook profile for admin review. Once approved, you'll receive a{' '}
                  <strong>Verified</strong> badge visible across the directory and your profile.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  noValidate
                  className="flex flex-col gap-5"
                >
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="linkedin_url">
                      LinkedIn or Facebook URL <span className="text-destructive" aria-hidden="true">*</span>
                    </Label>
                    <Input
                      id="linkedin_url"
                      type="url"
                      placeholder="https://linkedin.com/in/… or https://facebook.com/…"
                      aria-invalid={!!errors.linkedin_url}
                      {...register('linkedin_url')}
                    />
                    {errors.linkedin_url && (
                      <p role="alert" aria-live="polite" className="text-xs text-destructive">
                        {errors.linkedin_url.message}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="note">
                      Note to admin <span className="text-muted">(optional)</span>
                    </Label>
                    <Textarea
                      id="note"
                      placeholder="Anything that will help us verify your identity…"
                      rows={3}
                      aria-invalid={!!errors.note}
                      {...register('note')}
                    />
                    {errors.note && (
                      <p role="alert" aria-live="polite" className="text-xs text-destructive">
                        {errors.note.message}
                      </p>
                    )}
                  </div>

                  {submitError && (
                    <p
                      role="alert"
                      aria-live="polite"
                      className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive"
                    >
                      {submitError}
                    </p>
                  )}

                  <div className="flex items-center gap-2 rounded-xl bg-gold/10 px-4 py-3 text-xs text-navy">
                    <ShieldCheck className="h-4 w-4 shrink-0 text-gold-dark" aria-hidden="true" />
                    Admin review typically takes 1–3 business days.
                  </div>

                  <Button
                    type="submit"
                    variant="gold"
                    size="lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting…' : 'Request verification'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
